import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logOut, logUserIn } from '../redux/actions'
import { hasLiveSession, msUntilRefresh, TOKEN_STORAGE_KEY } from '../const/auth'

/**
 * Keeps the signed-in state honest: restores the session on load, refreshes the token
 * before it expires, and mirrors sign-in/sign-out made in other tabs.
 */
const SessionManager: React.FC = () => {
  const dispatch = useDispatch<any>()
  const isLoggedIn: boolean = useSelector((state: any) => state.user.isLoggedIn)
  const userData = useSelector((state: any) => state.user.data)

  // The reducer already starts signed out when no unexpired token is stored.
  useEffect(() => {
    if (hasLiveSession()) dispatch(logUserIn(true))
  }, [dispatch])

  // Each refresh replaces user.data, which re-arms the timer for the new token.
  useEffect(() => {
    if (!isLoggedIn) return
    const wait = msUntilRefresh()
    if (wait === null) {
      dispatch(logOut())
      return
    }
    const timer = window.setTimeout(() => dispatch(logUserIn()), wait)
    // Timers stall in background tabs and sleeping laptops; catch up when the page is seen again.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const left = msUntilRefresh()
      if (left === null) dispatch(logOut())
      else if (left === 0) dispatch(logUserIn())
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [dispatch, isLoggedIn, userData])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== TOKEN_STORAGE_KEY && e.key !== null) return
      if (!e.newValue || !hasLiveSession()) dispatch(logOut())
      else if (!e.oldValue) dispatch(logUserIn())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [dispatch])

  return null
}

export default SessionManager
